import { useCallback, useState } from "react";
import { Share2 } from "lucide-react";
import { siteBaseUrl } from "@/lib/seo";
import ShareModal from "./ShareModal";

/**
 * <ShareButton />: drop-in share trigger for public listings.
 *
 * Behaviour:
 *   - If the browser exposes `navigator.share` (mobile Safari, Chrome
 *     for Android, etc.) we call it directly so the user gets their
 *     OS share sheet (Messenger, WhatsApp, Viber, SMS, AirDrop, …).
 *   - Otherwise we open <ShareModal /> with Copy link, WhatsApp,
 *     Facebook (Share dialog), Viber, SMS and an X/Twitter shortcut.
 *
 * Props:
 *   slug       : canonical public slug (NOT an alias, NOT the /home variant).
 *                 We always build the canonical URL from siteBaseUrl(), which
 *                 returns the production base regardless of whether the user
 *                 is on confirm.ph or a preview/dev host.
 *   businessName, description: passed straight through to the share payload.
 *   variant    : "primary" | "secondary" | "ghost" | "icon"
 *   size       : "sm" | "md"
 *   label      : visible label (defaults to "Share")
 *   className  : extra classes
 *   testId     : data-testid override
 *   disabled   : disables the click (used when the public website is off)
 *   disabledReason: small tooltip text shown when disabled
 */
export default function ShareButton({
  slug,
  url,                 // optional override (e.g. owner dashboard wants the
                       // canonical for an as-yet-unactivated site)
  businessName = "ConfirmPH",
  description,
  variant = "secondary",
  size = "md",
  label = "Share",
  className = "",
  testId = "share-listing-btn",
  disabled = false,
  disabledReason = "",
}) {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState("");

  const canonicalUrl =
    url
      ? (/^https?:\/\//i.test(url) ? url : `${siteBaseUrl()}${url.startsWith("/") ? "" : "/"}${url}`)
      : `${siteBaseUrl()}/rentals/${(slug || "").toLowerCase()}`;

  const shareTitle =
    businessName === "ConfirmPH"
      ? "View this rental business on ConfirmPH"
      : `${businessName} on ConfirmPH`;
  const shareText = description?.trim()
    ? description.trim().slice(0, 180)
    : "View this rental business on ConfirmPH.";

  const openShare = useCallback(async () => {
    if (disabled) return;
    // Native Web Share API: best UX on mobile.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: canonicalUrl });
        return;
      } catch (err) {
        // AbortError (user dismissed) is fine; for anything else (eg.
        // permission denied) we fall back to the modal.
        if (err && err.name === "AbortError") return;
      }
    }
    setOpen(true);
  }, [canonicalUrl, shareTitle, shareText, disabled]);

  const base =
    "inline-flex items-center gap-2 rounded-lg font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes = size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm";
  const variants = {
    primary:   "bg-[#1B4332] text-white hover:bg-[#143027]",
    secondary: "bg-white text-[#1B4332] border border-[#1B4332]/30 hover:bg-[#1B4332]/5",
    ghost:     "bg-transparent text-slate-700 hover:bg-slate-100 border border-slate-200",
    icon:      "p-2 rounded-full bg-white/95 text-[#1B4332] border border-slate-200 hover:bg-slate-50",
  };
  const cls = variant === "icon"
    ? `${variants.icon} ${className}`
    : `${base} ${sizes} ${variants[variant] || variants.secondary} ${className}`;

  return (
    <>
      <button
        type="button"
        onClick={openShare}
        disabled={disabled}
        className={cls}
        data-testid={testId}
        aria-label={label}
        title={disabled ? (disabledReason || label) : label}
      >
        <Share2 className="w-4 h-4" />
        {variant !== "icon" && <span>{label}</span>}
      </button>

      {open && (
        <ShareModal
          url={canonicalUrl}
          title={shareTitle}
          text={shareText}
          onClose={() => setOpen(false)}
          onToast={(msg) => {
            setToast(msg);
            setTimeout(() => setToast(""), 1800);
          }}
        />
      )}

      {/* Tiny self-managed toast for "Link copied" feedback so we don't
          depend on the sonner toaster being mounted under every layout. */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-slate-900 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-2xl"
          data-testid="share-toast"
        >
          {toast}
        </div>
      )}
    </>
  );
}
